import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  RefreshCw,
  Play,
  Clock,
  Globe,
  Search,
  Calendar,
  History,
  Zap,
  Check,
  X,
  AlertCircle,
  Plus,
  Trash2,
  Link,
  Server
} from "lucide-react";
import { toast } from "sonner";
import AddScraperModal from "../components/AddScraperModal";

const AVAILABLE_SOURCES = [
  { value: "jsearch", label: "JSearch", color: "#6366f1", description: "Indeed, LinkedIn, Glassdoor aggregator (API)" },
  { value: "usajobs", label: "USAJobs", color: "#112e51", description: "Federal government positions (API)" },
  { value: "remoteok", label: "RemoteOK", color: "#2a9d8f", description: "Remote jobs worldwide (API)" },
  { value: "indeed", label: "Indeed", color: "#f4a261", description: "General job board (web scraping)" },
  { value: "dice", label: "Dice", color: "#e76f51", description: "Tech/IT jobs (web scraping)" },
  { value: "cleared_jobs", label: "ClearedJobs", color: "#40c9ba", description: "Security-cleared positions" },
  { value: "linkedin", label: "LinkedIn", color: "#0077b5", description: "Professional network (web scraping)" },
  { value: "sam_gov", label: "SAM.gov", color: "#e9c46a", description: "Federal contracts" },
];

const DEFAULT_KEYWORDS = [
  "AWS Cloud Engineer",
  "DevSecOps Engineer", 
  "Security Engineer",
  "GovCloud Architect",
  "Zero Trust Engineer",
  "Cloud Security"
];

const DATE_FILTER_OPTIONS = [
  { value: "today", label: "Last 24 hours", description: "Only jobs posted today" },
  { value: "3days", label: "Last 72 hours", description: "Jobs from the last 3 days" },
  { value: "week", label: "Last 7 days", description: "Jobs from the past week" },
];

export default function Scraper() {
  const [scraping, setScraping] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [schedule, setSchedule] = useState(null);
  const [activeJobs, setActiveJobs] = useState([]);
  
  // Custom scrapers
  const [customScrapers, setCustomScrapers] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [runningCustomScraper, setRunningCustomScraper] = useState(null);
  
  // Scrape config
  const [selectedSources, setSelectedSources] = useState(["jsearch", "usajobs", "remoteok"]);
  const [keywords, setKeywords] = useState(DEFAULT_KEYWORDS);
  const [newKeyword, setNewKeyword] = useState("");
  const [location, setLocation] = useState("Remote");
  
  // Schedule config
  const [scheduleType, setScheduleType] = useState("interval");
  const [scheduleHour, setScheduleHour] = useState(8);
  const [scheduleMinute, setScheduleMinute] = useState(0);
  const [intervalHours, setIntervalHours] = useState(4);
  const [datePosted, setDatePosted] = useState("3days");

  const fetchSchedule = async () => {
    try {
      const response = await api.get('/scrape/schedule');
      setActiveJobs(response.data.active_jobs || []);
      if (response.data.config) {
        const config = response.data.config;
        setSelectedSources(config.sources || []);
        setKeywords(config.keywords || DEFAULT_KEYWORDS);
        setLocation(config.location || "Remote");
        setScheduleType(config.schedule_type || "interval");
        setScheduleHour(config.hour || 8);
        setScheduleMinute(config.minute || 0);
        setIntervalHours(config.interval_hours || 4);
        setDatePosted(config.date_posted || "3days");
        setSchedule(config);
      }
    } catch (error) {
      console.error("Failed to fetch schedule:", error);
    }
  };

  const fetchHistory = async () => {
    try {
      const response = await api.get('/scrape/history?limit=10');
      setHistory(response.data.history || []);
    } catch (error) {
      console.error("Failed to fetch history:", error);
    }
  };

  const fetchCustomScrapers = async () => {
    try {
      const response = await api.get('/scrapers/custom');
      setCustomScrapers(response.data.scrapers || []);
    } catch (error) {
      console.error("Failed to fetch custom scrapers:", error);
    }
  };

  const handleDeleteCustomScraper = async (scraperId) => {
    try {
      await api.delete(`/scrapers/custom/${scraperId}`);
      toast.success("Scraper deleted");
      fetchCustomScrapers();
    } catch (error) {
      toast.error("Failed to delete scraper");
    }
  };

  const handleRunCustomScraper = async (scraper) => {
    setRunningCustomScraper(scraper.id);
    try {
      const response = await api.post(`/scrapers/custom/${scraper.id}/run`, null, {
        params: { keywords: keywords.join(","), location }
      });
      toast.success(`Found ${response.data.leads_found} leads, added ${response.data.leads_added} new`);
      fetchHistory();
    } catch (error) {
      toast.error("Failed to run scraper");
    } finally {
      setRunningCustomScraper(null);
    }
  };

  useEffect(() => {
    fetchSchedule();
    fetchHistory();
    fetchCustomScrapers();
  }, []);

  const handleScrapeNow = async () => {
    setScraping(true);
    try {
      const response = await api.post('/scrape', {
        sources: selectedSources,
        keywords: keywords,
        date_posted: datePosted
      });
      setLastResult(response.data);
      toast.success(`Scraping complete! Found ${response.data.leads_found} leads, added ${response.data.leads_added} new.`);
      fetchHistory();
    } catch (error) {
      toast.error("Scraping failed");
      console.error(error);
    } finally {
      setScraping(false);
    }
  };

  const handleSaveSchedule = async () => {
    setScheduling(true);
    try {
      const response = await api.post('/scrape/schedule', {
        sources: selectedSources,
        keywords: keywords,
        location: location,
        schedule_type: scheduleType,
        hour: scheduleHour,
        minute: scheduleMinute,
        interval_hours: intervalHours,
        date_posted: datePosted
      });
      toast.success("Schedule saved!");
      fetchSchedule();
    } catch (error) {
      toast.error("Failed to save schedule");
      console.error(error);
    } finally {
      setScheduling(false);
    }
  };

  const handleCancelSchedule = async (jobId) => {
    try {
      await api.delete(`/scrape/schedule/${jobId}`);
      toast.success("Schedule cancelled");
      fetchSchedule();
    } catch (error) {
      toast.error("Failed to cancel schedule");
      console.error(error);
    }
  };

  const toggleSource = (source) => {
    if (selectedSources.includes(source)) {
      setSelectedSources(selectedSources.filter(s => s !== source));
    } else {
      setSelectedSources([...selectedSources, source]);
    }
  };

  const addKeyword = () => {
    if (newKeyword && !keywords.includes(newKeyword)) {
      setKeywords([...keywords, newKeyword]);
      setNewKeyword("");
    }
  };

  const removeKeyword = (keyword) => {
    setKeywords(keywords.filter(k => k !== keyword));
  };

  return (
    <div className="space-y-6" data-testid="scraper-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="w-14 h-12 rounded-xl bg-gradient-to-br from-[#2a9d8f] to-[#40c9ba] flex items-center justify-center" style={{boxShadow: '4px 4px 0 rgba(26, 107, 98, 0.4)'}}>
            <Globe className="w-7 h-7 text-theme-dark" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-theme-light" style={{textShadow: '2px 2px 4px rgba(0,0,0,0.3)'}}>
              Job Scraper
            </h1>
            <p className="text-theme-muted text-sm font-mono">
              AUTOMATED CONTRACT HUNTING
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={() => setShowAddModal(true)}
            variant="outline"
            className="border-2 border-theme-primary/50 text-theme-primary-light hover:bg-theme-primary/20"
            style={{boxShadow: '3px 3px 0 rgba(42, 157, 143, 0.3)'}}
            data-testid="add-scraper-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            ADD SCRAPER
          </Button>
          <Button 
            onClick={handleScrapeNow}
            disabled={scraping || selectedSources.length === 0}
            className="bg-gradient-to-r from-[#e9c46a] to-[#d4a84b] hover:from-[#f4d888] hover:to-[#e9c46a] text-theme-dark border-2 border-[#f4d888]/50 font-bold"
            style={{boxShadow: '3px 3px 0 rgba(139, 69, 19, 0.3)'}}
            data-testid="scrape-now-btn"
          >
            {scraping ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Play className="w-4 h-4 mr-2" />
            )}
            {scraping ? "SCRAPING..." : "SCRAPE NOW"}
          </Button>
        </div>
      </div>

      {/* Add Scraper Modal */}
      <AddScraperModal 
        open={showAddModal} 
        onClose={() => setShowAddModal(false)} 
        onScraperAdded={fetchCustomScrapers}
      />

      {/* Last Result */}
      {lastResult && (
        <div className="p-4 rounded-xl" style={{
          background: lastResult.status === 'completed' 
            ? 'linear-gradient(145deg, rgba(42, 157, 143, 0.2), rgba(64, 201, 186, 0.1))'
            : 'linear-gradient(145deg, rgba(231, 111, 81, 0.2), rgba(244, 162, 97, 0.1))',
          border: `2px solid ${lastResult.status === 'completed' ? 'rgba(42, 157, 143, 0.4)' : 'rgba(231, 111, 81, 0.4)'}`,
        }}>
          <div className="flex items-center gap-3">
            {lastResult.status === 'completed' ? (
              <Check className="w-5 h-5 text-theme-primary" />
            ) : (
              <AlertCircle className="w-5 h-5 text-theme-danger" />
            )}
            <div>
              <p className="text-theme-light font-semibold">
                {lastResult.status === 'completed' ? 'Scrape Complete' : 'Scrape Failed'}
              </p>
              <p className="text-sm text-theme-muted font-mono">
                Found {lastResult.leads_found} leads • Added {lastResult.leads_added} new
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Sources Selection */}
        <div className="rounded-xl overflow-hidden" style={{
          background: 'linear-gradient(145deg, rgba(26, 58, 66, 0.9), rgba(38, 70, 83, 0.8))',
          border: '3px solid rgba(233, 196, 106, 0.3)',
          boxShadow: '6px 6px 0 rgba(139, 69, 19, 0.2)'
        }}>
          <div className="p-5 border-b-2 border-theme-secondary/30 bg-theme-darker/50">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#e9c46a] to-[#d4a84b] flex items-center justify-center">
                <Globe className="w-5 h-5 text-theme-dark" />
              </div>
              <div>
                <h2 className="text-theme-light font-bold">Job Sources</h2>
                <p className="text-xs text-theme-muted font-mono">SELECT SOURCES TO SCRAPE</p>
              </div>
            </div>
          </div>
          <div className="p-5 space-y-3">
            {AVAILABLE_SOURCES.map((source) => (
              <div 
                key={source.value}
                className={`p-4 rounded-lg cursor-pointer transition-all ${
                  selectedSources.includes(source.value)
                    ? 'bg-theme-primary/20 border-2 border-theme-primary/50'
                    : 'bg-theme-dark/50 border-2 border-transparent hover:border-theme-primary/30'
                }`}
                onClick={() => toggleSource(source.value)}
                data-testid={`source-${source.value}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: source.color }}
                    />
                    <div>
                      <span className="text-theme-light font-medium">{source.label}</span>
                      {source.description && (
                        <p className="text-xs text-theme-muted">{source.description}</p>
                      )}
                    </div>
                  </div>
                  <Switch 
                    checked={selectedSources.includes(source.value)}
                    onCheckedChange={() => toggleSource(source.value)}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Keywords */}
        <div className="rounded-xl overflow-hidden" style={{
          background: 'linear-gradient(145deg, rgba(26, 58, 66, 0.9), rgba(38, 70, 83, 0.8))',
          border: '3px solid rgba(42, 157, 143, 0.3)',
          boxShadow: '6px 6px 0 rgba(38, 70, 83, 0.3)'
        }}>
          <div className="p-5 border-b-2 border-theme-primary/30 bg-theme-darker/50">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#2a9d8f] to-[#1a6b62] flex items-center justify-center">
                <Search className="w-5 h-5 text-theme-secondary" />
              </div>
              <div>
                <h2 className="text-theme-light font-bold">Search Keywords</h2>
                <p className="text-xs text-theme-muted font-mono">JOB TITLES TO SEARCH</p>
              </div>
            </div>
          </div>
          <div className="p-5">
            <div className="flex flex-wrap gap-2 mb-4">
              {keywords.map((keyword) => (
                <Badge 
                  key={keyword}
                  className="bg-theme-primary/20 text-theme-primary-light border-2 border-theme-primary/40 pr-1 font-mono text-xs"
                >
                  {keyword}
                  <button 
                    onClick={() => removeKeyword(keyword)}
                    className="ml-1 hover:text-theme-danger transition-colors"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </Badge>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                value={newKeyword}
                onChange={(e) => setNewKeyword(e.target.value)}
                placeholder="Add keyword..."
                className="bg-theme-dark border-2 border-theme-primary/40 text-theme-light"
                onKeyDown={(e) => e.key === "Enter" && addKeyword()}
              />
              <Button 
                variant="outline" 
                className="border-2 border-theme-primary/40 text-theme-primary-light hover:bg-theme-primary/20"
                onClick={addKeyword}
              >
                <Zap className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Custom Scrapers Section */}
      {customScrapers.length > 0 && (
        <div className="rounded-xl overflow-hidden" style={{
          background: 'linear-gradient(145deg, rgba(26, 58, 66, 0.9), rgba(38, 70, 83, 0.8))',
          border: '3px solid rgba(231, 111, 81, 0.3)',
          boxShadow: '6px 6px 0 rgba(139, 69, 19, 0.2)'
        }}>
          <div className="p-5 border-b-2 border-theme-danger/30 bg-theme-darker/50">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#e76f51] to-[#d4533b] flex items-center justify-center">
                  <Plus className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-theme-light font-bold">Custom Scrapers</h2>
                  <p className="text-xs text-theme-muted font-mono">YOUR CUSTOM SOURCES</p>
                </div>
              </div>
              <Badge className="bg-theme-danger/20 text-theme-danger border border-theme-danger/40 font-mono">
                {customScrapers.length} SCRAPER{customScrapers.length !== 1 ? 'S' : ''}
              </Badge>
            </div>
          </div>
          <div className="p-5 space-y-3">
            {customScrapers.map((scraper) => (
              <div 
                key={scraper.id}
                className="p-4 rounded-lg bg-theme-dark/50 border-2 border-theme-danger/30 hover:border-theme-danger/50 transition-all"
                data-testid={`custom-scraper-${scraper.id}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                      scraper.scraper_type === 'job_board' ? 'bg-theme-primary/20' :
                      scraper.scraper_type === 'custom_url' ? 'bg-theme-accent/20' :
                      'bg-theme-danger/20'
                    }`}>
                      {scraper.scraper_type === 'job_board' ? (
                        <Globe className="w-4 h-4 text-theme-primary" />
                      ) : scraper.scraper_type === 'custom_url' ? (
                        <Link className="w-4 h-4 text-theme-accent" />
                      ) : (
                        <Server className="w-4 h-4 text-theme-danger" />
                      )}
                    </div>
                    <div>
                      <p className="text-theme-light font-medium">{scraper.name}</p>
                      <p className="text-xs text-theme-muted font-mono">
                        {scraper.scraper_type === 'job_board' ? 'Job Board' :
                         scraper.scraper_type === 'custom_url' ? 'Custom URL' : 'API Source'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      onClick={() => handleRunCustomScraper(scraper)}
                      disabled={runningCustomScraper === scraper.id}
                      className="bg-theme-primary hover:bg-[#40c9ba] text-white"
                      data-testid={`run-scraper-${scraper.id}`}
                    >
                      {runningCustomScraper === scraper.id ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <Play className="w-4 h-4" />
                      )}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDeleteCustomScraper(scraper.id)}
                      className="text-theme-danger hover:bg-theme-danger/20"
                      data-testid={`delete-scraper-${scraper.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Schedule Configuration */}
      <div className="rounded-xl overflow-hidden" style={{
        background: 'linear-gradient(145deg, rgba(26, 58, 66, 0.9), rgba(38, 70, 83, 0.8))',
        border: '3px solid rgba(244, 162, 97, 0.3)',
        boxShadow: '6px 6px 0 rgba(139, 69, 19, 0.2)'
      }}>
        <div className="p-5 border-b-2 border-theme-accent/30 bg-theme-darker/50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#f4a261] to-[#e76f51] flex items-center justify-center">
                <Clock className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-theme-light font-bold">Automated Schedule</h2>
                <p className="text-xs text-theme-muted font-mono">SET UP RECURRING SCRAPES</p>
              </div>
            </div>
            {activeJobs.length > 0 && (
              <Badge className="bg-theme-primary/20 text-theme-primary-light border-2 border-theme-primary/40 font-mono">
                {activeJobs.length} ACTIVE
              </Badge>
            )}
          </div>
        </div>
        <div className="p-5 space-y-6">
          {/* Active Jobs */}
          {activeJobs.length > 0 && (
            <div className="space-y-2">
              <Label className="text-theme-secondary font-mono text-xs">ACTIVE SCHEDULES</Label>
              {activeJobs.map((job) => (
                <div 
                  key={job.id}
                  className="p-3 rounded-lg bg-theme-dark/50 border-2 border-theme-primary/30 flex items-center justify-between"
                >
                  <div>
                    <p className="text-theme-light font-medium">{job.name}</p>
                    <p className="text-xs text-theme-muted font-mono">
                      Next run: {job.next_run ? new Date(job.next_run).toLocaleString() : 'N/A'}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-theme-danger hover:bg-theme-danger/20"
                    onClick={() => handleCancelSchedule(job.id)}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}

          {/* Schedule Type */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label className="text-theme-muted font-mono text-xs">SCHEDULE TYPE</Label>
              <Select value={scheduleType} onValueChange={setScheduleType}>
                <SelectTrigger className="bg-theme-dark border-2 border-theme-primary/40 text-theme-light mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-theme-dark border-2 border-theme-primary/40">
                  <SelectItem value="daily" className="text-theme-light">Daily</SelectItem>
                  <SelectItem value="interval" className="text-theme-light">Interval</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-theme-muted font-mono text-xs">JOB POSTING AGE</Label>
              <Select value={datePosted} onValueChange={setDatePosted}>
                <SelectTrigger className="bg-theme-dark border-2 border-theme-secondary/40 text-theme-light mt-1" data-testid="date-filter-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-theme-dark border-2 border-theme-secondary/40">
                  {DATE_FILTER_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value} className="text-theme-light">
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-theme-muted font-mono text-xs">LOCATION</Label>
              <Input
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                className="bg-theme-dark border-2 border-theme-primary/40 mt-1 text-theme-light"
              />
            </div>
          </div>

          {scheduleType === "daily" ? (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-theme-muted font-mono text-xs">HOUR (UTC)</Label>
                <Input
                  type="number"
                  min={0}
                  max={23}
                  value={scheduleHour}
                  onChange={(e) => setScheduleHour(parseInt(e.target.value))}
                  className="bg-theme-dark border-2 border-theme-primary/40 mt-1 text-theme-secondary font-mono"
                />
              </div>
              <div>
                <Label className="text-theme-muted font-mono text-xs">MINUTE</Label>
                <Input
                  type="number"
                  min={0}
                  max={59}
                  value={scheduleMinute}
                  onChange={(e) => setScheduleMinute(parseInt(e.target.value))}
                  className="bg-theme-dark border-2 border-theme-primary/40 mt-1 text-theme-secondary font-mono"
                />
              </div>
            </div>
          ) : (
            <div>
              <Label className="text-theme-muted font-mono text-xs">INTERVAL (HOURS)</Label>
              <Input
                type="number"
                min={1}
                max={24}
                value={intervalHours}
                onChange={(e) => setIntervalHours(parseInt(e.target.value))}
                className="bg-theme-dark border-2 border-theme-primary/40 mt-1 text-theme-secondary font-mono"
              />
            </div>
          )}

          <Button 
            onClick={handleSaveSchedule}
            disabled={scheduling || selectedSources.length === 0}
            className="w-full bg-gradient-to-r from-[#2a9d8f] to-[#1a6b62] hover:from-[#40c9ba] hover:to-[#2a9d8f] text-theme-light border-2 border-[#40c9ba]/50 font-bold"
            style={{boxShadow: '3px 3px 0 rgba(42, 157, 143, 0.4)'}}
          >
            {scheduling ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Calendar className="w-4 h-4 mr-2" />
            )}
            {scheduling ? "SAVING..." : "SAVE SCHEDULE"}
          </Button>
        </div>
      </div>

      {/* Scrape History */}
      {history.length > 0 && (
        <div className="rounded-xl overflow-hidden" style={{
          background: 'linear-gradient(145deg, rgba(26, 58, 66, 0.9), rgba(38, 70, 83, 0.8))',
          border: '3px solid rgba(64, 201, 186, 0.3)',
          boxShadow: '6px 6px 0 rgba(38, 70, 83, 0.3)'
        }}>
          <div className="p-5 border-b-2 border-[#40c9ba]/30 bg-theme-darker/50">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#40c9ba] to-[#2a9d8f] flex items-center justify-center">
                <History className="w-5 h-5 text-theme-dark" />
              </div>
              <div>
                <h2 className="text-theme-light font-bold">Scrape History</h2>
                <p className="text-xs text-theme-muted font-mono">RECENT SCRAPING ACTIVITY</p>
              </div>
            </div>
          </div>
          <div className="divide-y-2 divide-[#2a9d8f]/20">
            {history.map((item, index) => (
              <div key={index} className="p-4 hover:bg-theme-primary/10 transition-colors">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-theme-light font-medium">
                      {item.sources?.join(", ") || "Multiple sources"}
                    </p>
                    <p className="text-xs text-theme-muted font-mono">
                      {item.timestamp ? new Date(item.timestamp).toLocaleString() : "Unknown time"}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-theme-secondary font-mono font-bold">{item.leads_found || 0} found</p>
                    <p className="text-xs text-theme-primary-light font-mono">{item.leads_added || 0} added</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
